import React, { useMemo } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import { UI_BODY_SM, UI_TAG, UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Lightning } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { useLensesV2Context } from "../LensesV2Context";
import { useTagCooccurrence } from "@/components/analysisModules/AnnotationExplorer/comps/AnnotationExplorerVizPanel/queries/useTagCooccurrence";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { useAggrMetricsAcrossTimeAndDimension } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetLineChart/query/useAggrMetricsAcrossTimeAndDimension";
import { TrendPill } from "../comps/TrendPill";
import { TrendLineChart } from "../comps/TrendLineChart";
import { SignalBadge } from "../comps/SignalBadge";
import { SIGNAL_TOPIC_SIDES, toTagCountMap, COOCCURRENCE_FULL } from "../utils";
import { assembleSignals, narrowToSignals } from "./utils";
import { CONFIG } from "./CONFIG";

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const SignalList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SignalCard = styled.div`
  padding: 1.2rem 0.5rem;
  border-bottom: 1px solid ${colors2.grey5};

  &:last-child {
    border-bottom: none;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
`;

const MentionCount = styled(UI_CAPTION_SEMIBOLD_DF)`
  color: ${colors2.grey40};
`;

const ContextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: 1rem;
`;

const ContextTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  background: ${colors2.grey5};
  color: ${colors2.grey60};
`;

// Signals widget (list) — per signal: count, period-over-period trend, top
// co-occurring topics. Same v1 SignalsLensBlock list UI (numbered, category-
// coloured badge, mention count, trend pill, topic chips), minus the over-time
// chart (skipped for now) and the drilldown sidebar. Rides on the SAME
// cooccurrence + prev-count queries the Topics widget makes (shared cache).
export const SignalsWidget = () => {
  const { findQuery, prevFindQuery, period, signals: signalList, dateFieldMap } = useLensesV2Context();

  const cooccurrence = useTagCooccurrence({
    sides: SIGNAL_TOPIC_SIDES,
    ...COOCCURRENCE_FULL, // full unfiltered set — no top-N slice
    findQuery,
    pinSideBAsTagB: true, // signal → tagA, topic → tagB
    enabled: !!findQuery,
  });

  // Previous same-length window — trend only needs standalone signal counts,
  // not pairs, so a plain per-tag count aggregation (lighter than cooccurrence).
  // SAME call the Topics widget makes → shared react-query cache.
  const prevCounts = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: ["annotations.tags.tags"],
    findQuery: prevFindQuery,
    vizCardId: "lensV2PrevTagCounts",
    queryOptions: { enabled: !!prevFindQuery },
  });

  // Signal counts over time — generic aggr endpoint, called directly; narrowed
  // to signals on the FE. No bespoke backend route. (The per-content-type date
  // path is inherently sparse — the BE skips the complete-timeline fill — so we
  // don't pass includeNullDimensions; the viz layer builds the full axis.)
  const overTime = useAggrMetricsAcrossTimeAndDimension({
    contentType: "annotations",
    dateValuePathByContentType: dateFieldMap,
    // Span the axis over the selected period (out to `now` for relative periods),
    // so empty intervals show as zero rather than the series stopping at the last
    // data point. Required for the per-CT date path — it can't derive its own range.
    timelineRange: period ? { from: period.from, to: period.to } : undefined,
    dimensionValuePath: "annotations.tags.tags",
    interval: period?.interval,
    findQuery,
    vizCardId: "lensV2SignalTime",
    queryOptions: { enabled: !!findQuery },
  });

  const signals = useMemo(
    () => assembleSignals(signalList, cooccurrence.data, toTagCountMap(prevCounts.data)),
    [signalList, cooccurrence.data, prevCounts.data]
  );

  // Narrowed to signal tags — ready for the over-time chart (chart render is WIP).
  const signalsOverTime = useMemo(
    () => narrowToSignals(overTime.data, signalList),
    [overTime.data, signalList]
  );

  // Colour each signal line by its category, from the SAME CONFIG the badges use
  // (so line colour == badge colour). Keyed by the signal _id — the stable key
  // the aggr dimension carries as its value.
  const signalColors = useMemo(() => {
    const map = {};
    for (const s of signalList || []) {
      map[String(s._id)] = (CONFIG.categoryStyle[s.category] || CONFIG.defaultCategoryStyle).badgeBg;
    }
    return map;
  }, [signalList]);

  return (
    <DashboardWidgetShell
      titleJSX={
        <TitleWrapper>
          <Lightning size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Signals</UI_BODY_SEMIBOLD_SM_DF>
        </TitleWrapper>
      }
      style={{ gap: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: 520 }}
    >
      {overTime.isLoading ? (
        <div style={{ marginBottom: "1rem" }}>
          <SkeletonLoader style={{ height: 240, width: "100%", borderRadius: 8 }} />
        </div>
      ) : signalsOverTime.length > 1 ? (
        <div style={{ marginBottom: "1rem", position: "sticky", top: 0, zIndex: 1, background: colors2.white }}>
          <TrendLineChart data={signalsOverTime} interval={period?.interval} colors={signalColors} />
        </div>
      ) : null}

      {cooccurrence.isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ padding: "8px 0" }}>
              <SkeletonLoader style={{ height: 22, width: 80, borderRadius: 12, marginBottom: 8 }} />
              <SkeletonLoader style={{ height: 12, width: "90%", borderRadius: 4, marginBottom: 4 }} />
              <SkeletonLoader style={{ height: 12, width: "70%", borderRadius: 4 }} />
            </div>
          ))}
        </div>
      )}

      {cooccurrence.isError && (
        <UI_BODY_SM style={{ color: "#dc2626" }}>
          Error: {cooccurrence.error?.response?.data?.message || cooccurrence.error?.message}
        </UI_BODY_SM>
      )}

      {cooccurrence.data && signals.length === 0 && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No signal data available yet.</UI_BODY_SM>
      )}

      {cooccurrence.data && signals.length > 0 && (
        <SignalList>
          {signals.map((signal, idx) => {
            const style = CONFIG.categoryStyle[signal.category] || CONFIG.defaultCategoryStyle;
            return (
              <SignalCard key={signal.signalId}>
                <BadgeRow>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", width: "100%", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                      <UI_CAPTION style={{ color: colors2.grey40 }}>{idx + 1}</UI_CAPTION>
                      <SignalBadge category={signal.category} label={signal.signal} />
                      <MentionCount>{signal.total} mentions</MentionCount>
                    </div>
                    <TrendPill trend={signal.trend} variant="trend" tone={style.tone} iconSize={14} />
                  </div>
                </BadgeRow>
                {signal.topics?.length > 0 && (
                  <ContextRow>
                    {signal.topics.map((t) => (
                      <ContextTag key={t.topicId}>
                        <UI_TAG>{t.topic}</UI_TAG>
                      </ContextTag>
                    ))}
                  </ContextRow>
                )}
              </SignalCard>
            );
          })}
        </SignalList>
      )}
    </DashboardWidgetShell>
  );
};
